import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';
import { V3MemberInfoDialogComponent } from '@app/app/page-modules/admin/components/members/admin-member-list/v3-member-info-dialog/v3-member-info-dialog.component';
import { MemberInfoDialogData } from '@app/app/page-modules/admin/components/members/model/members.model';
import {
  UpdateCardPaginationFilters,
  UpdateMemberPaginationFilters,
  UpdatePlaylistPaginationFilters,
} from '@app/app/page-modules/admin/components/members/store/members.action';
import { PaginationFilter } from '@app/app/page-modules/admin/components/members/store/members.state.model';
import { Members, MembersSearchOrder } from '@app/app/shared/models/admin/members.model';
import { takeUntil } from 'rxjs/operators';
import { AdminMemberTableComponent } from '../admin-member-table/admin-member-table.component';

@Component({
  selector: 'ptl-v3-admin-member-list',
  templateUrl: './v3-admin-member-list.component.html',
  styleUrls: ['./v3-admin-member-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class V3AdminMemberListComponent extends AdminMemberTableComponent {
  @Input() cardUid: string;
  @Input() playlistUid: string;

  @Output() refreshList = new EventEmitter<number>();

  get defaultTable(): AdminMemberTableComponent {
    return this;
  }

  setIsLoaderActive(active: boolean) {
    this.isLoaderActive = active;
  }

  setPageSize(size: number) {
    this.pageSize = size;
  }

  setCurrentPage(page: number) {
    this.currentPage = page;
  }

  override onPageChangeEvent(event: { currentPage: number; pageSize: number }): void {
    super.onPageChangeEvent(event);
    this.persistPagination();
  }

  override onSizeChange(size: number): void {
    super.onSizeChange(size);
    this.persistPagination();
  }

  override changeListOrdering(orderBy: string): void {
    super.changeListOrdering(orderBy);
    this.persistPagination();
  }

  getMemberInitials(firstName: string, lastName: string): string {
    const first = firstName?.trim().charAt(0) ?? '';
    const last = lastName?.trim().charAt(0) ?? '';
    return (first + last).toUpperCase();
  }

  getRoleLabel(roles: string[]): string {
    return roles?.length ? roles[0] : '';
  }

  isMemberActive(member: Members): boolean {
    const lastActive = member?.lastActiveOn ? new Date(member.lastActiveOn).getTime() : 0;
    if (!lastActive) {
      return false;
    }
    const oneWeekMs = 7 * 24 * 60 * 60 * 1000;
    return Date.now() - lastActive <= oneWeekMs;
  }

  private persistPagination(): void {
    const order: MembersSearchOrder = {
      fieldName: this.defaultOrderBy,
      direction: this.order,
    };
    const paginationFilter: PaginationFilter = {
      page: this.currentPage,
      pageSize: this.pageSize,
      order,
    };

    if (this.isCardPage) {
      this.store.dispatch(new UpdateCardPaginationFilters(this.cardUid, paginationFilter));
    } else if (this.isPlaylistPage) {
      this.store.dispatch(new UpdatePlaylistPaginationFilters(this.playlistUid, paginationFilter));
    } else {
      this.store.dispatch(new UpdateMemberPaginationFilters(paginationFilter));
    }
  }

  override openUserRecordByUid(member: Members): void {
    const memberInfoData: MemberInfoDialogData = {
      member: member,
      isPlaylistPage: this.isPlaylistPage,
      isCardPage: this.isCardPage,
      parentUrl: this.router.url,
      query: this.booleanSearchRequest,
    };

    const dialogRef = this.dialog.open<V3MemberInfoDialogComponent, MemberInfoDialogData>(V3MemberInfoDialogComponent, {
      width: '23.75rem',
      height: '100vh',
      disableClose: true,
      position: { right: '0', top: '0' },
      panelClass: ['slide-in-dialog', 'f_slide-in-dialog', 'member-info-dialog'],
      data: memberInfoData,
    });

    dialogRef
      .backdropClick()
      .pipe(takeUntil(dialogRef.afterClosed()))
      .subscribe(() => {
        const overlayPane = document.querySelector('.f_slide-in-dialog');

        if (overlayPane) {
          overlayPane.classList.add('slide-out');
        }

        setTimeout(() => {
          dialogRef.close();
        }, 300);
      });
  }
}
